import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import SettlementLines from "@/components/finance/SettlementLines";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ledgerService } from "@/services/ledgerService";
import { payableService } from "@/services/payableService";
import { handleApiError } from "@/services/api";
import type { SupplierLedger } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";
import { formatApplyResult } from "@/lib/utils";

const methodLabels: Record<string, string> = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const statusColor: Record<string, "success" | "warning" | "error"> = {
  paid: "success",
  partial: "warning",
  unpaid: "error",
};

function formatCurrency(n: number) {
  return "\u20B9" + Math.abs(n).toLocaleString("en-IN");
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(dateStr: string) {
  const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return "Today";
  return `${diff}d`;
}

export default function SupplierLedgerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Recording money is OWNER-only in the API (payments, advances, apply
  // credit). Gating on "not a viewer" showed a manager or supervisor the
  // forms and let them fill one in, then refused the save with a 403.
  const canRecordMoney = useIsOwner();
  const [confirmDeletePayment, setConfirmDeletePayment] = useState<number | null>(null);
  // ?highlight=<paymentId> — set when arriving from a bank-ledger match link.
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get("highlight")) || null;

  const [ledger, setLedger] = useState<SupplierLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showApplyCredit, setShowApplyCredit] = useState(false);
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [applyResult, setApplyResult] = useState<string>("");

  const fetchLedger = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await ledgerService.getSupplierLedger(parseInt(id));
      setLedger(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Scroll the highlighted (bank-linked) payment/advance row into view.
  useEffect(() => {
    if (highlightId == null || !ledger) return;
    const row = document.querySelector(".ledger-row-highlight");
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, ledger]);

  const handleApplyCredit = async () => {
    if (!id) return;
    setApplyingCredit(true);
    setApplyResult("");
    try {
      const res = await payableService.applyCredit(parseInt(id));
      setApplyResult(formatApplyResult(res));
      await fetchLedger();
    } catch (err) {
      setApplyResult(handleApiError(err));
    } finally {
      setApplyingCredit(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageMeta title="Supplier Ledger | Poultry Book" description="Supplier transaction history" />
        <PageBreadcrumb pageTitle="Supplier Ledger" />
        <div className="py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
      </>
    );
  }

  if (error && !ledger) {
    return (
      <>
        <PageMeta title="Supplier Ledger | Poultry Book" description="Supplier transaction history" />
        <PageBreadcrumb pageTitle="Supplier Ledger" />
        <div className="py-10 text-center text-error-500">{error}</div>
      </>
    );
  }

  if (!ledger) return null;

  const { supplier, summary } = ledger;
  const upcoming_dues = ledger.upcoming_dues ?? [];
  const entries = ledger.entries ?? [];

  return (
    <>
      <PageMeta title={`${supplier.name} Ledger | Poultry Book`} description="Supplier transaction history" />
      <PageBreadcrumb pageTitle={supplier.name} />

      {/* Back link */}
      <button
        onClick={() => navigate("/payables")}
        className="mb-4 flex items-center gap-1 text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
      >
        &larr; Back to Accounts Payable
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">{supplier.name}</h2>
          {supplier.phone && <p className="text-sm text-gray-500 dark:text-gray-400">{supplier.phone}</p>}
        </div>
        {supplier.credit_days != null && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Credit: {supplier.credit_days} days
          </span>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-900/20 dark:text-error-400">{error}</div>}

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Billed</p>
          <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">{formatCurrency(summary.total_billed)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Paid</p>
          <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">{formatCurrency(summary.total_paid)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Net Balance</p>
          <p className={`mt-1 text-lg font-semibold ${summary.net_balance > 0 ? "text-error-600 dark:text-error-400" : summary.net_balance < 0 ? "text-success-600 dark:text-success-400" : "text-gray-800 dark:text-white/90"}`}>
            {formatCurrency(summary.net_balance)}
            {summary.net_balance > 0 && <span className="ml-1 text-xs font-normal">(You Owe)</span>}
            {summary.net_balance < 0 && <span className="ml-1 text-xs font-normal">(Credit)</span>}
          </p>
          {/* Plan §11.8: explain the gap between Net Balance and the
              sum of per-bill Outstanding. The advance pool reduces Net
              Balance immediately on tag, but each bill stays unpaid
              until Pay-modal opt-in consumes the credit against it. */}
          {summary.available_credit > 0 && (
            <p className="mt-1 text-xs text-brand-600 dark:text-brand-400" data-testid="available-credit-line">
              {"₹"}{summary.available_credit.toLocaleString("en-IN")} available credit
              <span className="text-gray-400">
                {" "}from {summary.available_credit_count} earlier payment{summary.available_credit_count === 1 ? "" : "s"}
              </span>
            </p>
          )}
          {/* Shown when there is credit AND open bills — gross open bills =
              net_balance + available_credit (net already subtracts the credit). */}
          {/* Kept visible (no flag): credit held from before Record Payment
              settled oldest-first has no other way onto bills already open. */}
          {canRecordMoney && summary.available_credit > 0 && summary.net_balance + summary.available_credit > 0 && (
            <button
              type="button"
              onClick={() => { setApplyResult(""); setShowApplyCredit(true); }}
              className="mt-2 rounded-md bg-gray-900 dark:bg-white px-2.5 py-1 text-xs font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              Apply credit to bills
            </button>
          )}
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Due Soon</p>
          <p className="mt-1 text-lg font-semibold text-orange-600 dark:text-orange-400">
            {summary.due_soon_count > 0 ? (
              <>{formatCurrency(summary.due_soon_amount)} <span className="text-xs font-normal">({summary.due_soon_count} bills)</span></>
            ) : summary.overdue_count > 0 ? (
              <>{formatCurrency(summary.overdue_amount)} <span className="text-xs font-normal text-error-600 dark:text-error-400">({summary.overdue_count} overdue)</span></>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </p>
        </div>
      </div>

      {/* Upcoming Dues */}
      {upcoming_dues.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Upcoming Dues</h3>
          <div className="space-y-2">
            {upcoming_dues.map((p) => {
              const outstanding = p.total_amount - p.paid_amount;
              const isOverdue = p.due_date ? new Date(p.due_date) < new Date() : false;
              return (
                <div
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 list-row px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {p.bill_number || `Bill #${p.id}`}
                    </span>
                    <span className="text-sm text-gray-500">{formatCurrency(outstanding)} outstanding</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.due_date && (
                      <span className={`text-sm ${isOverdue ? "font-medium text-error-600 dark:text-error-400" : "text-gray-500 dark:text-gray-400"}`}>
                        Due: {formatDate(p.due_date)} ({daysUntil(p.due_date)})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ledger Table */}
      <div className="overflow-hidden card">
        <h3 className="border-b border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 dark:border-white/[0.05] dark:text-gray-300">
          Ledger
        </h3>
        {entries.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No transactions yet</div>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell isHeader>Date</TableCell>
                  <TableCell isHeader>Particulars</TableCell>
                  <TableCell isHeader>Debit</TableCell>
                  <TableCell isHeader>Credit</TableCell>
                  <TableCell isHeader>Balance</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e, i) => {
                  // A payment/advance row matches the ?highlight target (a payment
                  // id); bills are excluded since their ids share the sequence.
                  const isHighlight =
                    highlightId != null && e.id === highlightId && (e.type === "payment" || e.type === "advance");
                  return (
                  <TableRow
                    key={`${e.type}-${e.id}-${i}`}
                    className={isHighlight ? "ledger-row-highlight bg-brand-50 dark:bg-brand-500/10" : undefined}
                  >
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(e.date)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {e.type === "bill" && (
                          <>
                            <span className="font-medium">Bill</span>
                            {e.bill_number && <span className="text-gray-500">: {e.bill_number}</span>}
                            {e.status && (
                              <> <Badge color={statusColor[e.status] ?? "light"}>
                                {e.status}
                              </Badge></>
                            )}
                          </>
                        )}
                        {e.type === "payment" && (
                          <>
                            <span className="font-medium">Payment</span>
                            {e.method && <span className="text-gray-500"> - {methodLabels[e.method] ?? e.method}</span>}
                            {e.reference && <span className="text-gray-400"> ({e.reference})</span>}
                            {e.parent_payment_id != null && (
                              <span
                                className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                title="Settled by leftover credit carried forward from an earlier overpayment."
                                data-testid={`leftover-credit-badge-${e.id}`}
                              >
                                leftover credit
                              </span>
                            )}
                          </>
                        )}
                        {e.type === "advance" && (
                          <>
                            <span className="font-medium">Payment</span>
                            {e.method && <span className="text-gray-500"> - {methodLabels[e.method] ?? e.method}</span>}
                            {e.reference && <span className="text-gray-400"> ({e.reference})</span>}
                            {e.source_type === "bank_statement_transaction" && (
                              <span
                                className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                title="Tagged from bank statement — to delete or correct, use Unmatch on the bank ledger row."
                                data-testid={`from-bank-badge-${e.id}`}
                              >
                                from bank
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {e.notes && <p className="mt-0.5 text-xs text-gray-400">{e.notes}</p>}
                      {e.type === "advance" && <SettlementLines settled={e.settled} held={e.held} docLabel="Bill" />}
                      {e.type === "advance" && canRecordMoney && !e.source_type && (
                        <button
                          type="button"
                          onClick={() => setConfirmDeletePayment(e.id)}
                          className="mt-1 text-xs font-medium text-error-500 hover:text-error-600 dark:text-error-400"
                          data-testid={`delete-payment-${e.id}`}
                        >
                          Delete
                        </button>
                      )}
                      {e.type === "payment" && e.parent_payment_id != null && (
                        <p
                          className="mt-0.5 text-xs text-brand-600 dark:text-brand-400"
                          data-testid={`leftover-credit-line-${e.id}`}
                        >
                          From payment #{e.parent_payment_id} · paid {formatDate(e.date)}
                          {e.applied_at && formatDate(e.applied_at) !== formatDate(e.date) && (
                            <> · applied {formatDate(e.applied_at)}</>
                          )}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className={e.debit > 0 ? "font-medium text-error-600 dark:text-error-400" : ""}>
                      {e.debit > 0 ? formatCurrency(e.debit) : ""}
                    </TableCell>
                    <TableCell className={e.credit > 0 ? "font-medium text-success-600 dark:text-success-400" : ""}>
                      {e.credit > 0 ? formatCurrency(e.credit) : ""}
                    </TableCell>
                    <TableCell className={`font-medium ${e.balance > 0 ? "text-error-600 dark:text-error-400" : e.balance < 0 ? "text-success-600 dark:text-success-400" : ""}`}>
                      {formatCurrency(e.balance)}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDeletePayment !== null}
        title="Delete payment"
        message="The bills it settled go back to unpaid (or part-paid), and any credit it left is removed."
        onClose={() => setConfirmDeletePayment(null)}
        onConfirm={async () => {
          if (confirmDeletePayment === null) return;
          try {
            await payableService.deletePayment(confirmDeletePayment);
            setConfirmDeletePayment(null);
            fetchLedger();
          } catch (err) {
            setConfirmDeletePayment(null);
            setError(handleApiError(err));
          }
        }}
      />

      {/* Record Payment Modal */}
      {/* Apply-credit confirm — preview then confirm (not silent). */}
      <Modal isOpen={showApplyCredit} onClose={() => setShowApplyCredit(false)} className="max-w-md">
        <div className="p-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">Apply credit to bills</h3>
          {ledger && (
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Apply <span className="font-semibold">{formatCurrency(ledger.summary.available_credit)}</span> of{" "}
              {ledger.supplier.name}&rsquo;s advance credit across their open bills, <span className="font-medium">oldest first</span>.
              Up to <span className="font-semibold">{formatCurrency(Math.min(ledger.summary.available_credit, ledger.summary.net_balance + ledger.summary.available_credit))}</span> will be settled; any excess stays as credit.
            </p>
          )}
          {applyResult && (
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-white/[0.06] dark:text-gray-200" data-testid="apply-credit-result">
              {applyResult}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowApplyCredit(false)}>
              {applyResult ? "Close" : "Cancel"}
            </Button>
            {!applyResult && (
              <Button size="sm" onClick={handleApplyCredit} disabled={applyingCredit}>
                {applyingCredit ? "Applying…" : "Apply credit"}
              </Button>
            )}
          </div>
        </div>
      </Modal>

    </>
  );
}
